import React from "react";
import { OriAvatar, ORI_ORB_CROP_SCALE } from "components/ori";
import { Message, MessageAgenda } from "../types";
import { humaniseAgenda } from "apps/user/learner/lib/ori-agenda-label";
import { useTranslation } from "lib/i18n/use-translation";
import OriMessageBubble from "./OriMessageBubble";
import OriTypingIndicator from "./OriTypingIndicator";
import styles from "./OriMessageList.module.scss";

const SEAM_TIME_FORMAT = new Intl.DateTimeFormat(undefined, {
  hour: "2-digit",
  minute: "2-digit",
});

interface OriMessageListProps {
  messages: Message[];
  /** A turn is in flight, so the last assistant bubble is still being written. */
  isSending?: boolean;
  isAwaitingReply?: boolean;
  isLoadingHistory?: boolean;
  /** A greeting is still on its way, so an empty transcript is "not yet".
   *  History settling is not enough: the socket carrying the greeting only
   *  opens afterwards, and the welcome would show for the whole handshake. */
  isGreetingPending?: boolean;
  activityStatus?: string | null;
  // `ori_worded_loading`: show the worded breath default in place of the dots.
  wordedLoading?: boolean;
  firstName?: string;
  scrollRef: React.RefObject<HTMLDivElement>;
  onScroll: () => void;
  // QA only: mark where Ori moved between agendas. Bound by the caller to an
  // open bounded thread, so a learner's transcript never carries the markers
  // (nor the agenda stamps they read).
  showAgendaSeams?: boolean;
  // Rendered after the last bubble, or under the welcome on an empty
  // transcript: the Coach-served starters on Ori's newest message for a
  // returning learner, or the generic starters for one who has never
  // messaged Ori.
  starterPills?: React.ReactNode;
}

const OriEmptyState: React.FC<{
  firstName?: string;
  children?: React.ReactNode;
}> = ({ firstName, children }) => {
  const { t } = useTranslation(["ori"]);
  return (
    <div className={styles.emptyState} data-testid="ori-empty-state">
      <OriAvatar
        size={42}
        cropScale={ORI_ORB_CROP_SCALE}
        testId="ori-empty-state-avatar"
      />
      <h2 className={styles.emptyHeading}>
        {firstName
          ? t("ori:chat.emptyState.greetingNamed", { name: firstName })
          : t("ori:chat.emptyState.greeting")}
      </h2>
      {children && <div className={styles.emptyStarters}>{children}</div>}
    </div>
  );
};

// Where Ori moved to another agenda. This is metadata about the thread, not
// something anyone said, so it borrows the date-divider idiom — a hairline rule
// with a small centred label — rather than anything bubble- or badge-shaped.
const OriAgendaSeam: React.FC<{ agenda: MessageAgenda }> = ({ agenda }) => {
  const { t } = useTranslation(["ori"]);
  return (
    <div className={styles.seam} data-testid="ori-agenda-seam">
      <span className={styles.seamRow}>
        <span className={styles.seamRule} aria-hidden />
        <span className={styles.seamLabel}>{humaniseAgenda(agenda.type)}</span>
        <span className={styles.seamRule} aria-hidden />
      </span>
      <span className={styles.seamMeta}>
        {t("ori:qa.seam.movedHere", {
          time: SEAM_TIME_FORMAT.format(new Date(agenda.startedAt)),
        })}
      </span>
    </div>
  );
};

const OriMessageList: React.FC<OriMessageListProps> = ({
  messages,
  isSending = false,
  isAwaitingReply = false,
  isLoadingHistory = false,
  isGreetingPending = false,
  activityStatus = null,
  wordedLoading = false,
  firstName,
  scrollRef,
  onScroll,
  showAgendaSeams = false,
  starterPills = null,
}) => {
  // The status line can also arrive mid-stream (after message_start), when
  // isAwaitingReply is already false — it still owns the indicator slot then.
  const showIndicatorSlot = isAwaitingReply || activityStatus !== null;
  // The worded phrases read as reactions to a conversation, so the very first
  // wait (before any message has content) keeps the neutral dots instead.
  const conversationStarted = messages.some(
    (message) => message.content !== ""
  );
  // An empty transcript only means "nothing here" once nothing more is coming:
  // history has settled and no greeting is in flight. Without the greeting
  // check the welcome shows for the whole socket handshake, which only starts
  // after history settles (WONE-2709).
  const isEmpty =
    !isLoadingHistory &&
    !isGreetingPending &&
    messages.length === 0 &&
    !isAwaitingReply;

  return (
    <div
      ref={scrollRef}
      className={styles.list}
      data-testid="ori-message-list"
      onScroll={onScroll}
    >
      {isEmpty ? (
        <OriEmptyState firstName={firstName}>{starterPills}</OriEmptyState>
      ) : (
        <div className={styles.inner}>
          {messages.map((message, index) => {
            const previous = messages[index - 1];
            // Only a change *between* two stamped messages is a boundary: the
            // first message opens the transcript rather than moving anywhere,
            // and live-streamed messages carry no stamp at all.
            const seam =
              showAgendaSeams &&
              message.agenda &&
              previous?.agenda &&
              message.agenda.type !== previous.agenda.type
                ? message.agenda
                : null;
            return (
              <React.Fragment
                key={message.message_id ?? `${message.role}-${index}`}
              >
                {seam && <OriAgendaSeam agenda={seam} />}
                <OriMessageBubble
                  message={message}
                  showFeedback={
                    message.role === "assistant" &&
                    !(index === messages.length - 1 && isSending)
                  }
                />
              </React.Fragment>
            );
          })}
          {showIndicatorSlot && (
            <OriTypingIndicator
              statusText={activityStatus}
              wordedDefault={wordedLoading && conversationStarted}
            />
          )}
          {starterPills}
        </div>
      )}
    </div>
  );
};

export default OriMessageList;
